/**
 * The paywall step, with everything that can happen on it.
 *
 * One component owns the state the handoff's exit ladder needs, because the
 * rules are about ordering rather than about any single screen: the promo
 * countdown starts once and never restarts, the interception fires once, the
 * discount step fires once and only if a live discount exists, and after that
 * leaving is simply allowed.
 *
 * Returning from Polar without a payment lands here too — the recovery screen
 * renders over the paywall rather than instead of it, so dismissing it puts the
 * person back exactly where they were.
 */
import React from 'react';
import { UserProfile } from '../types/quiz';
import { useTt } from '../i18n/useT';
import { startPolarCheckout } from '../lib/polar';
import { trackPixel } from '../utils/trackEvent';
import { PRICE_MONTH, PromoState, verifyPromo } from '../config/paywall';
import { useAppContent } from '../app/content';
import { PaywallProfile } from './data';
import Paywall from './Paywall';
import Recovery from './Recovery';
import { Downsell, ExitIntent } from './ExitLadder';
import { trackPaywall, PaywallContext } from './analytics';
import {
    checkoutPending,
    clearCheckoutPending,
    flags,
    grantPromo,
    markCheckoutPending,
    promoGrantedAt,
    saveAcceptedPromo,
    useExitIntent,
    usePromoTimer,
} from './session';

/** Bumped whenever the funnel's shape changes, so analytics can compare builds. */
const FUNNEL_VARIANT = 'paywall-en-v1';

interface Props {
    profile: PaywallProfile;
    userProfile: UserProfile;
    quizId?: string;
}

const PaywallStage: React.FC<Props> = ({ profile, userProfile, quizId }) => {
    const { t } = useTt();
    const { content } = useAppContent();

    // The live offer, resolved below. Null means no discount is configured, and
    // every promo-shaped element on the page disappears with it.
    const [promo, setPromo] = React.useState<PromoState | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [exitOpen, setExitOpen] = React.useState(false);
    const [downsellOpen, setDownsellOpen] = React.useState(false);
    const [recovering, setRecovering] = React.useState(false);

    const grantedAt = promoGrantedAt();
    const { label: timerLabel, expired } = usePromoTimer(grantedAt);

    const ctx: PaywallContext = React.useMemo(
        () => ({
            quizId,
            profile,
            promo,
            variant: FUNNEL_VARIANT,
            email: typeof userProfile.email === 'string' ? userProfile.email : undefined,
        }),
        [quizId, profile, promo, userProfile.email],
    );

    // Read by effects that must not re-run when the context changes.
    const ctxRef = React.useRef(ctx);

    ctxRef.current = ctx;

    /**
     * Resolve the offer this person is actually on.
     *
     * The discount belongs to anyone who reaches the paywall, not only to those
     * who went through the scratch screen: that screen reveals the offer, it
     * does not create it. Someone who resumed a draft, deep-linked, or saw the
     * scratch step skipped would otherwise land on a weaker page — a two-step
     * price ladder and no countdown — while the checkout still applied the
     * discount. The page and the till have to agree.
     *
     * Everything stays keyed to the live Polar discount: no live discount, no
     * promo bar, no countdown, no discounted row. `grantPromo` is idempotent,
     * so a deadline set on the scratch screen keeps running rather than
     * restarting here.
     */
    React.useEffect(() => {
        let live = true;

        verifyPromo().then((found) => {
            if (!live || !found.valid) return;
            const first = !promoGrantedAt();

            saveAcceptedPromo(found.code, found.percent);
            grantPromo();
            setPromo(found);
            if (first) trackPaywall('promo_granted', ctxRef.current, { percent: found.percent, source: 'paywall' });
        });

        return () => {
            live = false;
        };
    }, []);

    // Fires once per mount. The ref guards React 18's double-invoke in dev.
    const viewed = React.useRef(false);

    React.useEffect(() => {
        if (viewed.current || !profile.ready) return;
        viewed.current = true;
        trackPaywall('paywall_viewed', ctx);
    }, [ctx, profile.ready]);

    // Coming back from Polar with the pending flag still set means the person
    // reached the hosted checkout and left without paying.
    //
    // Checked on mount AND on `pageshow`, because iOS Safari restores the page
    // from its back/forward cache: the browser comes back without React ever
    // remounting, so a mount-only check would miss exactly the case this screen
    // exists for.
    React.useEffect(() => {
        const check = () => {
            // Whatever else happened, the browser is here and interactive, so
            // nothing may still be waiting on a redirect. `busy` was set on the
            // way OUT to Polar; the back/forward cache restores it along with
            // the rest of the JS state, which left every button on this screen
            // permanently greyed out.
            setBusy(false);
            setError(null);

            if (!checkoutPending()) return;
            clearCheckoutPending();
            setRecovering(true);
            trackPaywall('checkout_cancelled', ctxRef.current);
            trackPaywall('checkout_recovery_viewed', ctxRef.current);
        };

        check();
        window.addEventListener('pageshow', check);
        return () => window.removeEventListener('pageshow', check);
    }, []);

    const expiredSeen = React.useRef(false);

    React.useEffect(() => {
        if (!expired || !grantedAt || expiredSeen.current) return;
        expiredSeen.current = true;
        trackPaywall('promo_expired', ctx);
    }, [expired, grantedAt, ctx]);

    /**
     * The interception stays armed for as long as the ladder has a rung left.
     *
     * Whether it may actually fire *right now* is a separate question, answered
     * through a ref rather than by disarming: `busy` and `recovering` flip often,
     * and re-arming on each flip is what previously tore the hook down mid-flow.
     */
    const ladderSpent = flags.exitIntentShown() && (flags.downsellShown() || !promo?.valid);
    const blocked = exitOpen || downsellOpen || recovering || busy;
    const blockedRef = React.useRef(blocked);

    blockedRef.current = blocked;

    useExitIntent(!ladderSpent, () => {
        if (blockedRef.current) return;
        trackPaywall('exit_intent_triggered', ctx);
        if (!flags.exitIntentShown()) {
            flags.markExitIntentShown();
            setExitOpen(true);
            trackPaywall('exit_modal_viewed', ctx);

            return;
        }
        if (promo?.valid && !flags.downsellShown()) {
            flags.markDownsellShown();
            setDownsellOpen(true);
            trackPaywall('downsell_viewed', ctx);
        }
    });

    const checkout = React.useCallback(
        async (source: string) => {
            if (busy) return;
            setBusy(true);
            setError(null);

            const email = typeof userProfile.email === 'string' ? userProfile.email : undefined;
            // The discount only rides along while it is still valid AND the
            // window it was granted for has not run out.
            const code = promo?.valid && !expired ? promo.code : undefined;

            trackPaywall('checkout_initiated', ctx, { source, discountCode: code || null });
            trackPixel('track', 'InitiateCheckout', { value: PRICE_MONTH, currency: 'USD' });

            try {
                await startPolarCheckout({
                    email,
                    returnPath: `/subscription-success/${quizId || 'doyourstep-quiz'}`,
                    discountCode: code,
                    onSession: ({ discountApplied }) => {
                        // Set immediately before the browser leaves, so coming
                        // back without a success is detectable on the next mount.
                        markCheckoutPending();
                        trackPaywall('trial_started', ctx, { discountApplied, source });
                    },
                    metadata: {
                        quizId: quizId || '',
                        source: 'paywall',
                        primaryTrack: profile.dirCode || '',
                        topPattern: profile.blocker?.code || '',
                        funnelVariant: FUNNEL_VARIANT,
                        userEmail: email || '',
                    },
                });
            } catch (e) {
                // Leaving `busy` set would strand the person on a dead button.
                setBusy(false);
                setError(t('pw.checkout.failed'));
                // eslint-disable-next-line no-console
                console.error('Polar checkout error:', e);
            }
        },
        [busy, ctx, expired, profile, promo, quizId, t, userProfile.email],
    );

    const counts = {
        patterns: content ? content.patterns.length : 0,
        exercises: content ? content.exercises.length : 0,
    };

    return (
        <>
            <Paywall
                profile={profile}
                promo={promo && !expired ? promo : null}
                timer={promo?.valid && grantedAt && !expired ? timerLabel : null}
                counts={counts}
                onCheckout={() => checkout('paywall')}
                busy={busy}
                error={error}
            />

            <ExitIntent
                open={exitOpen}
                profile={profile}
                onStay={() => {
                    setExitOpen(false);
                    trackPaywall('exit_modal_continue_clicked', ctx);
                }}
                onLeave={() => {
                    setExitOpen(false);
                    trackPaywall('exit_modal_leave_clicked', ctx);
                    // Rung 3: offer the discount, but only when one exists. If it
                    // does not, rung 4 applies and the person simply leaves.
                    if (promo?.valid && !flags.downsellShown()) {
                        flags.markDownsellShown();
                        setDownsellOpen(true);
                        trackPaywall('downsell_viewed', ctx);
                    }
                }}
            />

            {promo?.valid && (
                <Downsell
                    open={downsellOpen}
                    promo={promo}
                    onAccept={() => {
                        setDownsellOpen(false);
                        trackPaywall('downsell_accepted', ctx);
                        checkout('downsell');
                    }}
                    onLeave={() => {
                        setDownsellOpen(false);
                        trackPaywall('downsell_rejected', ctx);
                    }}
                    onDismiss={() => setDownsellOpen(false)}
                />
            )}

            {/*
             * Accepting here returns to the paywall rather than firing another
             * checkout. This person has just come back from Polar's card form;
             * bouncing them straight into it again is the one move guaranteed
             * to feel like a trap. The offer, the price ladder and the
             * guarantee are all one screen away, and the CTA there still works.
             */}
            {recovering && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 80, overflowY: 'auto' }}>
                    <Recovery
                        onAccept={() => {
                            trackPaywall('checkout_recovery_accepted', ctx);
                            setRecovering(false);
                            window.scrollTo({ top: 0 });
                        }}
                        onDismiss={() => setRecovering(false)}
                    />
                </div>
            )}
        </>
    );
};

export default PaywallStage;
